/**
 * Authentication Service
 * Handles all authentication-related API calls
 */
import api, { getApiBaseUrl, getFallbackBaseUrl, setApiBaseUrl } from './api';

const authService = {
  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise} API response
   */
  register: async (userData) => {
    return api.post('/auth/register', userData);
  },

  /**
   * Login user
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} API response with token and user data
   */
  login: async (email, password) => {
    try {
      return await api.post('/auth/login', { email, password });
    } catch (error) {
      const currentBaseUrl = getApiBaseUrl();
      const fallbackBaseUrl = getFallbackBaseUrl();
      const shouldRetry =
        error.response?.status === 404 &&
        fallbackBaseUrl &&
        fallbackBaseUrl !== currentBaseUrl;

      if (shouldRetry) {
        setApiBaseUrl(fallbackBaseUrl);
        try {
          return await api.post('/auth/login', { email, password });
        } catch (retryError) {
          setApiBaseUrl(currentBaseUrl);
          throw retryError;
        }
      }

      throw error;
    }
  },

  sendMobileOtp: async (mobile, deliveryChannel = 'whatsapp') => {
    return api.post('/auth/mobile-otp/send', { mobile, delivery_channel: deliveryChannel });
  },
  sendMobileOtpForLogin: async (mobile, deliveryChannel = 'whatsapp') => {
    return api.post('/auth/mobile-otp/login-send', { mobile, delivery_channel: deliveryChannel });
  },

  verifyMobileOtp: async (mobile, otp) => {
    return api.post('/auth/mobile-otp/verify', { mobile, otp });
  },

  loginWithMobileOtp: async (mobile, otp) => {
    return api.post('/auth/mobile-otp/login', { mobile, otp });
  },

  /**
   * Login/Register user with Google credential
   * @param {Object} payload - Google auth payload
   * @returns {Promise} API response with token and user data
   */
  googleAuth: async (payload) => {
    return api.post('/auth/google', payload);
  },

  appleAuth: async (payload) => {
    return api.post('/auth/apple', payload);
  },

  selectRole: async (role) => {
    return api.post('/auth/select-role', { role });
  },

  /**
   * Verify access code
   * @param {string} code - Access code to verify
   * @returns {Promise} API response with user data
   */
  verifyAccessCode: async (code) => {
    return api.post('/auth/verify-access-code', { code });
  },

  /**
   * Change user password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise} API response
   */
  changePassword: async (currentPassword, newPassword) => {
    return api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },

  /**
   * Update user profile
   * @param {Object} profileData - Profile data to update
   * @returns {Promise} API response
   */
  updateProfile: async (profileData) => {
    return api.post('/auth/update-profile', profileData);
  },

  /**
   * Request account deletion (7-day grace period)
   * @param {Object} payload - password and optional reason
   * @returns {Promise} API response
   */
  requestAccountDeletion: async (payload) => {
    return api.post('/auth/request-account-deletion', payload);
  },

  /**
   * Cancel pending account deletion
   * @returns {Promise} API response
   */
  cancelAccountDeletion: async () => {
    return api.post('/auth/cancel-account-deletion');
  },

  /**
   * Logout user (client-side only)
   * Clears local storage
   */
  logout: () => {
    localStorage.removeItem('edsupp_token');
    localStorage.removeItem('edsupp_user');
  },

  /**
   * Get current user from localStorage
   * @returns {Object|null} User object or null
   */
  getCurrentUser: () => {
    const userStr = localStorage.getItem('edsupp_user');
    return userStr ? JSON.parse(userStr) : null;
  },

  /**
   * Get current token from localStorage
   * @returns {string|null} Token or null
   */
  getToken: () => {
    return localStorage.getItem('edsupp_token');
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} True if user has valid token
   */
  isAuthenticated: () => {
    return !!localStorage.getItem('edsupp_token');
  },
};

export default authService;
